import { useState, useLayoutEffect, useRef } from 'react';
import cn from 'classnames';

import Image from 'components/image/Image';
import RandomShapes from 'components/nysgjerrigper/quotes-block/RandomShapes';

import { IQuotesBlock } from 'source/NFR.types';

interface QuotesBlockProps extends IQuotesBlock {
  blockIndex: number;
  stepIndex: number;
  stepsContentNode: HTMLElement | null;
}

const QuotesBlock: React.FC<QuotesBlockProps> = ({ title, quotes, blockIndex, stepIndex, stepsContentNode }) => {
  const [shouldAnimateIn, setShouldAnimateIn] = useState<boolean[]>(quotes?.map(() => false) || []);
  const [parallaxScroll, setParallaxScroll] = useState<number[]>(
    Array.from({ length: Math.max(0, (quotes?.length ?? 0) - 1) }, () => 0)
  );

  const quoteRefs = useRef<(HTMLDivElement | null)[]>([]);
  const randomShapesRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Keep arrays sized to current lengths
  if (quoteRefs.current.length !== (quotes?.length || 0)) {
    quoteRefs.current = Array.from({ length: quotes.length }, (_, i) => quoteRefs.current[i] || null);
  }
  if (randomShapesRefs.current.length !== Math.max(0, (quotes?.length || 0) - 1)) {
    randomShapesRefs.current = Array.from(
      { length: Math.max(0, quotes.length - 1) },
      (_, i) => randomShapesRefs.current[i] || null
    );
  }

  useLayoutEffect(() => {
    if (!stepsContentNode) return;

    const topPosition = (el: HTMLElement | null): number =>
      el ? el.getBoundingClientRect().top - window.innerHeight : Number.POSITIVE_INFINITY;

    const onScroll = () => {
      const quotePositions = quoteRefs.current.map(el => topPosition(el));
      setShouldAnimateIn(state => {
        const nextState = quotePositions.map(nodePos => nodePos < 0);
        const needsToUpdate = nextState.some((shouldAnimateIn, i) => shouldAnimateIn !== state[i]);
        if (needsToUpdate) return nextState;
        return state;
      });

      const randomShapePositions = randomShapesRefs.current.map(el => topPosition(el));
      setParallaxScroll(state => {
        const nextState = randomShapePositions.map((nodePos, i) => {
          if (nodePos >= 0) return state[i] || 0;
          const scrollAmountWhileInView = -nodePos;
          const speed = 0.5;
          return -(scrollAmountWhileInView * speed);
        });

        const needsToUpdate = nextState.some((parallaxScroll, i) => parallaxScroll !== state[i]);
        if (needsToUpdate) return nextState;
        return state;
      });
    };

    stepsContentNode.addEventListener('scroll', onScroll);
    return () => stepsContentNode.removeEventListener('scroll', onScroll);
  }, [stepsContentNode]);

  if (!quotes || !quotes.length) return null;

  return (
    <div className="quotes-block">
      {title && <h2 className="quotes-block--title">{title}</h2>}
      {quotes?.map(({ image, text, quotee, addQuoteDash }, index) => (
        <div className="quotes-block--quote" key={quotee || index}>
          <div
            className={cn('quotes-block--content', {
              '-animate-in': shouldAnimateIn[index]
            })}
            ref={el => (quoteRefs.current[index] = el)}
          >
            {image && <Image className="quotes-block--image" {...image} responsive={false} />}
            <div className="quotes-block--texts">
              <p className="quotes-block--paragraph">{text}</p>
              {quotee && <div>{addQuoteDash ? `–${quotee}` : quotee}</div>}
            </div>
          </div>
          {index < quotes.length - 1 && (
            <RandomShapes
              ref={el => (randomShapesRefs.current[index] = el)}
              index={index}
              blockIndex={blockIndex}
              stepIndex={stepIndex}
              style={{ transform: `translateY(${parallaxScroll[index]}px)` }}
            />
          )}
        </div>
      ))}
    </div>
  );
};

QuotesBlock.displayName = 'QuotesBlock';

export default QuotesBlock;
